import {
  createRoute,
  createRouter,
  Outlet,
  lazyRouteComponent,
  redirect,
  createRootRouteWithContext,
  useSearch,
  type ParsedLocation,
} from "@tanstack/react-router";
import { z } from "zod/v4";

import { DashboardShell } from "layouts/bootShell/BootShell";
import { BootShellUntilRootLoads } from "layouts/bootShell/BootShellUntilRootLoads";
import { ConfirmEmail } from "pages/register/ConfirmEmail";
import { SignUpPage } from "pages/register/SignUpPage";
import { LoginPage } from "pages/register/LoginPage";
import { ResetPassword } from "pages/register/ResetPassword";
import { SuccessLayout } from "layouts/SuccessLayout";
import type { FetchPolicy } from "@apollo/client";
import { client } from "api/client";
import { logout } from "api/logout";
import { parseSelectedProjectId } from "utils/parseSelectedProjectId";
import { getProjectIdFromPath } from "utils/getProjectIdFromPath";
import {
  GET_ALL_PROJECTS,
  GET_ALL_SERVICES,
  GET_PROJECT,
  GET_USER,
} from "api/query";
import { PublicLayout } from "./layouts/PublicLayout";
import { InternalFeatures } from "./routes/InternalFeatures";
import type { IdpErrorReasons } from "api/constants";
import { SERVICES_QUERY_TIMEOUT_MS } from "api/constants";
import { readUiState } from "api/uiState";
import {
  getFromLocalStorage,
  LATEST_CAMPAIGN,
  LOCAL_STORAGE_USER,
  POST_VERIFY_REDIRECT_URL,
  REDIRECT_URL,
  STORAGE_TYPE,
  storeDirectlyIntoStorage,
  UI_STATE,
  URL_SEARCH_PARAMS,
} from "utils/localStorage";
import { type RouterContext, useAuth } from "./routes/AuthProvider";
import { AnalyticsIdentity } from "layouts/AnalyticsIdentity";
import { ErrorCatch } from "./routes/ErrorCatch";
import { when } from "mobx";
import { APP_STATE, commonStore } from "stores/commonStore";
import { useMount } from "react-use";
import { Loader } from "./components/progress/Loader";
import { Center } from "@chakra-ui/react";
import type {
  InstanceRole,
  JoinProjectMutation,
  JoinProjectMutationVariables,
  ServiceEnvironment,
  SupportBillingCategory,
  SupportCaseType,
  SupportServiceCapability,
} from "graphql/generated";
import {
  VirtualPlanType,
  ServicePlan,
  type GetAllProjectsQuery,
  type GetAllServicesQuery,
  type GetAllServicesQueryVariables,
  type GetProjectQuery,
  type GetProjectQueryVariables,
  type GetUserQuery,
} from "graphql/generated";
import type {
  ExplorerGeneralSearch,
  ExplorerHomeTab,
  ExplorerObjectSearch,
  ServiceLogsSearch,
} from "./routes/searchParams";
import { redirectIfAtExplorerRoot } from "pages/project/service/explorer/explorerIndexRedirect";
import { BranchOverviewSkeleton } from "pages/project/branch/BranchOverviewSkeleton";
import { ExplorerRouteSkeleton } from "pages/project/service/explorer/ExplorerSkeleton";
import { isSupportedBrowser } from "utils/browserSupport";
import { history } from "utils/history";
import { FEAT_HIDE_DATA_VIEW } from "utils/featuresGates";
import { isGateEnabled } from "utils/statsig";
import { syncProject } from "utils/syncProject";
import { selectBestProjectId } from "utils/selectBestProjectId";
import { showJoinProjectResult } from "utils/projectInvitations";
import { notificationStore } from "stores/notificationStore";
import { JOIN_PROJECT } from "api/mutations";

const Root = () => {
  const { campaign, utm_campaign } = useSearch({ strict: false });
  const { isAuthenticated } = useAuth();

  useMount(() => {
    // Prioritize campaign over utm_campaign until we remove campaign.
    const queryCampaign = campaign || utm_campaign;
    if (queryCampaign) {
      const campaignItems = queryCampaign
        ?.replace(" ", "")
        ?.split(",")
        .filter((s) => s);
      storeDirectlyIntoStorage(
        LATEST_CAMPAIGN,
        campaignItems,
        URL_SEARCH_PARAMS,
        STORAGE_TYPE.LOCAL,
      );
    }
  });

  return (
    <>
      <Outlet />
      <InternalFeatures />
      {isAuthenticated && <AnalyticsIdentity />}
    </>
  );
};

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Root,
  beforeLoad: ({ location }) => {
    // ESM-capable but below-target browsers boot the app, then land here and
    // get sent to the unsupported-browser page. Browsers too old to load the
    // bundle never reach this — they're handled by the `nomodule` guard in
    // index.html (see `unsupportedBrowserGuard` in vite.config.ts).
    if (location.pathname !== "/unsupported-browser" && !isSupportedBrowser()) {
      throw redirect({ to: "/unsupported-browser" });
    }
  },
});

/**
 * Takes the URL that unauthenticated users
 * navigated to/tried to access pre-login and
 * saves it to session and local storage.
 */
const storeRedirectURL = (pathname: string, searchStr: string): void => {
  // Do not overwrite an existing stored route
  if (sessionStorage.getItem(REDIRECT_URL)) {
    return;
  }

  try {
    sessionStorage.setItem(REDIRECT_URL, pathname + searchStr);
  } catch (e) {
    // If quota exceeded or storage unavailable
    console.log("Failed to store redirect URL in sessionStorage:", e);
  }

  if (pathname === "/oauth/authorize") {
    // CLI OAuth flow:
    // Users arriving via `tiger auth login` may not have an account and will sign up.
    // After email verification, they must return to the original URL to finish OAuth.
    // Because sessionStorage does not persist across tabs (common during verification),
    // also store the target in localStorage (with a short TTL).
    try {
      localStorage.setItem(
        POST_VERIFY_REDIRECT_URL,
        JSON.stringify({
          target: pathname + searchStr,
          timestamp: Date.now(),
        }),
      );
    } catch (e) {
      // If local storage quota was exceeded / storage unavailable.
      // OAuth flow will still work, but post-verification redirect won't
      console.log("Failed to store redirect URL in localStorage:", e);
    }
  }
};

/**
 * Redirects unauthenticated users to the login page,
 * and stores the URL they were trying to access
 * pre-login to session and local storage.
 *
 * When authenticated, they'll be redirected to that
 * URL. See {@link handleAuthenticatedAccess}
 */
const handleUnauthenticatedAccess = (
  pathname: string,
  searchStr: string,
): never => {
  storeRedirectURL(pathname, searchStr);

  throw redirect({
    to: "/login",
  });
};

/**
 * @function handleAuthenticatedAccess
 *
 * Redirects authenticated users back to the
 * route they tried to access pre-login.
 */
const handleAuthenticatedAccess = (
  client: RouterContext["client"],
  currentPathname?: string,
): void => {
  // The user must complete plan selection first.
  // Then where ever they navigate to afterwards,
  // we take them to their stored redirect URL.
  if (currentPathname === "/welcome") {
    return;
  }

  const redirectUrl = sessionStorage.getItem(REDIRECT_URL);
  if (!redirectUrl) {
    return;
  }

  // Project routes now carry a `{$projectId}` segment (see `projectLayout`).
  // A stored URL from before that change (e.g. `/projects/services/<id>/overview`
  // from a logged-out deep link) no longer matches any route, so the active
  // projectId must be inserted to preserve the section and deep path.
  if (isLegacyProjectUrl(redirectUrl)) {
    const projectId = resolveSelectedProjectId(client);

    if (!projectId) {
      if (currentPathname !== "/projects") {
        throw redirect({ to: "/projects" });
      }
      return;
    }

    sessionStorage.removeItem(REDIRECT_URL);
    localStorage.removeItem(POST_VERIFY_REDIRECT_URL);
    throw redirect({
      to: toProjectScopedUrl(redirectUrl, projectId),
    });
  }

  sessionStorage.removeItem(REDIRECT_URL);
  localStorage.removeItem(POST_VERIFY_REDIRECT_URL);
  throw redirect({
    to: redirectUrl,
  });
};

// Project-scoped sections whose bare `/projects/<section>` form needs the active
// projectId inserted. Covers pre-projectId URLs (`/projects/services/...`) and
// legacy top-level sections that moved under the project (`support`, `cli-mcp`,
// e.g. `/dashboard/support` -> `/projects/<projectId>/support`). `account` is
// excluded because it remains a projectId-less route.
const LEGACY_PROJECT_SECTIONS = new Set([
  "services",
  "billing",
  "security",
  "integrations",
  "activity",
  "members",
  "data-view",
  "settings",
  "no_services",
  "create_services",
  "support",
  "cli-mcp",
]);

/**
 * Detects a pre-projectId URL like `/projects/services/...` where the segment
 * after `projects` is a known project section rather than a projectId.
 */
const isLegacyProjectUrl = (url: string): boolean => {
  const pathname = url.split("?")[0];
  const [, prefix, section] = pathname.split("/");
  return prefix === "projects" && LEGACY_PROJECT_SECTIONS.has(section);
};

const toProjectScopedUrl = (url: string, projectId: string): string =>
  url.replace("/projects/", `/projects/${projectId}/`);

/**
 * Auth-only gate for `authLayout`: redirects unauthenticated users and restores
 * any stored pre-login redirect URL. Project-dependent guards (plan / project
 * sync) run lower down, in `projectLayout`, once the URL projectId is known.
 */
const checkAuthOnly = ({
  context,
  location,
}: {
  context: RouterContext;
  location: ParsedLocation;
}) => {
  const { pathname, searchStr } = location;

  if (!context.auth.isAuthenticated) {
    handleUnauthenticatedAccess(pathname, searchStr);
  }

  // If the user just logged in after previously trying to access an
  // authenticated route, redirect them back to the route they were
  // trying to access (or were previously on, if they were logged out
  // due to inactivity - see redirectUrl logic in logout()).
  handleAuthenticatedAccess(context.client, pathname);
};

/**
 * Auth + plan gate, used by routes that are NOT under `projectLayout` and so
 * don't get its plan guard: the oAuth layout and `/welcome`.
 */
const checkAuth = async (opts: {
  context: RouterContext;
  location: ParsedLocation;
}) => {
  checkAuthOnly(opts);
  await checkAwaitingPlanGuard(opts);
};

// Read the services list from the cache. The guard redirects only after the
// list loads. The `projectLayout` loader fetches the list.
const checkFreeServiceGuard = ({ params, context }: any) => {
  const service = (context as RouterContext).client
    .readQuery<GetAllServicesQuery, GetAllServicesQueryVariables>({
      query: GET_ALL_SERVICES,
      variables: { projectId: params.projectId },
    })
    ?.getAllServices.find(({ id }) => id === params.serviceId);
  if (service?.servicePlan === ServicePlan.Free) {
    throw redirect({
      to: "/projects/{$projectId}/services",
      params: { projectId: params.projectId },
    });
  }
};

/**
 * Used for redirecting project owners with
 * a plan type of AWAITING_PLAN to /welcome. Reads the URL's project or, on
 * routes without one, the project that the user has selected.
 */
const checkAwaitingPlanGuard = async ({
  context,
  location,
  params,
}: {
  context: RouterContext;
  location: ParsedLocation;
  params?: { projectId?: string };
}) => {
  await whenAppReady();
  const projectId =
    params?.projectId ?? (await resolveProjectId(context.client));
  const project = projectId
    ? await loadProject(context.client, projectId)
    : null;

  const isAwaitingPlan =
    project?.entitlementSpec?.plan_type === VirtualPlanType.AwaitingPlan;

  if (location.pathname === "/welcome") {
    if (!isAwaitingPlan) {
      // User has already clicked the CTA in WelcomePage (which
      // changes their plan from AWAITING_PLAN to Performance),
      // redirect to dashboard.
      throw redirect({
        to: "/projects",
      });
    }
    // Else, keep them on /welcome page
    return;
  }

  // Non-owners (e.g. invited users) shouldn't
  // be redirected to the Welcome/trial info page.
  if (!project?.isOwner) {
    return;
  }

  if (isAwaitingPlan) {
    // Store the current URL before redirecting to Welcome/trial summary page.
    //
    // This preserves the OAuth redirect for /oauth/authorize if
    // a user logins in via the CLI but hasn't selected a plan yet/
    // is taken to /welcome.
    const { pathname, searchStr } = location;
    storeRedirectURL(pathname, searchStr);

    throw redirect({
      to: "/welcome",
    });
  }
};

/**
 * Holds `AuthLayout` until the login-time store setup is done
 * (`APP_STATE.READY`, set by `authStore` once the user has loaded). Waiting in
 * the loader rather than inside the layout lets `authLayout`'s
 * `pendingComponent` cover the wait, and lets child routes show
 * their own pending components under the real nav once it renders.
 */
const whenAppReady = () => when(() => commonStore.appState === APP_STATE.READY);

/**
 * Loads a project from the Apollo cache, or from the API when it is not cached
 * or `fetchPolicy` asks for fresh data. `useProject` reads the same document,
 * so pages render from the cache. Returns null when the user cannot open the
 * project, or when the request fails.
 */
const loadProject = async (
  client: RouterContext["client"],
  projectId: string,
  fetchPolicy: FetchPolicy = "cache-first",
): Promise<GetProjectQuery["getProject"] | null> => {
  try {
    const { data } = await client.query<
      GetProjectQuery,
      GetProjectQueryVariables
    >({ query: GET_PROJECT, variables: { id: projectId }, fetchPolicy });
    return data?.getProject ?? null;
  } catch {
    return null;
  }
};

/**
 * The project to open on routes without a `{$projectId}` segment. Undefined
 * when the user has no project.
 */
const resolveProjectId = async (
  client: RouterContext["client"],
): Promise<string | undefined> => {
  const { data } = await client.query<GetAllProjectsQuery>({
    query: GET_ALL_PROJECTS,
  });
  return selectBestProjectId(
    data?.getAllProjects ?? [],
    resolveSelectedProjectId(client),
  );
};

/**
 * The project to open when the one in the URL is not available. It reads a
 * fresh list, so that a project that was deleted or left is not an option.
 */
const resolveFallbackProjectId = async (
  client: RouterContext["client"],
  unavailableProjectId: string,
): Promise<string | undefined> => {
  const { data } = await client.query<GetAllProjectsQuery>({
    query: GET_ALL_PROJECTS,
    fetchPolicy: "network-only",
  });
  return selectBestProjectId(
    (data?.getAllProjects ?? []).filter(
      ({ id }) => id !== unavailableProjectId,
    ),
    resolveSelectedProjectId(client),
  );
};

/**
 * Sets the Statsig user and the saved selection on routes without a
 * `{$projectId}` segment, as `syncAndValidateProject` does on project routes.
 * Does nothing for a user without a project: `/projects` creates one.
 */
const syncSelectedProject = async (client: RouterContext["client"]) => {
  const projectId = await resolveProjectId(client);
  const project = projectId ? await loadProject(client, projectId) : null;
  if (project) await syncProject(client, project);
};

/**
 * Checks the project named in the `{$projectId}` route param. Runs in
 * `projectLayout.beforeLoad`, so every project route resolves against the
 * project in the URL (the source of truth). Invalid / inaccessible ids fall
 * back to another project, or to `/projects` when there is none.
 */
const syncAndValidateProject = async ({
  context,
  params,
}: {
  context: RouterContext;
  params: { projectId: string };
}) => {
  const { projectId } = params;

  // Wait until the login setup has loaded the user and their saved selection.
  await whenAppReady();

  // A switch to another project loads it again, so that its plan and the
  // user's role are current.
  const isSwitch = readUiState(context.client).selectedProjectId !== projectId;
  const project = await loadProject(
    context.client,
    projectId,
    isSwitch ? "network-only" : "cache-first",
  );
  if (project) {
    await syncProject(context.client, project);
    return;
  }

  const fallbackProjectId = await resolveFallbackProjectId(
    context.client,
    projectId,
  );
  if (fallbackProjectId) {
    throw redirect({
      to: "/projects/{$projectId}/services",
      params: { projectId: fallbackProjectId },
    });
  }
  // Nothing to fall back to: `/projects` creates the user's first project.
  throw redirect({ to: "/projects" });
};

const loadServices = async (
  client: RouterContext["client"],
  projectId: string,
) => {
  try {
    await client.query({
      query: GET_ALL_SERVICES,
      variables: { projectId },
      context: { timeout: SERVICES_QUERY_TIMEOUT_MS },
    });
  } catch {
    // A network failure. `useServicesPoll` retries. `useAllServices` shows the
    // error. The route must still load, so the page can show it.
  }
};

const LoadingSpinner = () => (
  <Center h="100vh">
    <Loader size="lg" />
  </Center>
);

/* Redirect routes */

const indexRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/login" });
  },
});

const registerRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/register",
  beforeLoad: () => {
    throw redirect({
      to: "/signup",
    });
  },
});

const signinRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signin",
  beforeLoad: () => {
    throw redirect({ to: "/login" });
  },
});

const idpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/idp",
  component: lazyRouteComponent(
    () => import("pages/register/IDPLanding"),
    "IDPLanding",
  ),
  validateSearch: (search: { userId?: string; firstTime?: string }) => search,
});

const logoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/logout",
  component: LoadingSpinner,
  beforeLoad: async () => {
    await logout();
  },
});

/** Public routes */

// Where users land after an account deletion request.
const accountDeletionResultRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account-deletion-result",
  validateSearch: (search: { email?: string; error?: boolean }) => search,
  component: lazyRouteComponent(
    () => import("pages/account/AccountDeletionResult"),
    "AccountDeletionResult",
  ),
});

const publicLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: "publicLayout",
  component: PublicLayout,
  beforeLoad: ({ context }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: "/projects" });
    }
  },
});

const loginRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: "/login",
  component: LoginPage,
  // `accountDeleted` says the visitor has just deleted their account: the
  // page hands them to the report instead of a form they can't use.
  // `email` travels with it, since the stores are gone by then.
  validateSearch: (search: { email?: string; accountDeleted?: boolean }) =>
    search,
});

// Accepts single or repeated UTM params (arrays from
// duplicate query string keys are coerced to the first value so the router
// does not throw a validation error for URLs like ?utm_campaign=a&utm_campaign=b).
const utmString = z
  .union([z.string(), z.array(z.string())])
  .optional()
  .transform((v) => (Array.isArray(v) ? v[0] : v));

export const signupSearchSchema = z.object({
  email: z
    .string()
    .transform((v) => v.replace("_plusSign_", "+"))
    .optional(),
  hash: z.string().optional(),
  id: z.union([z.string(), z.number()]).optional(),
  default_plan: z.string().optional(),

  campaign: z.string().optional(),
  utm_campaign: utmString,
  utm_content: utmString,
  utm_keyword: utmString,
  utm_medium: utmString,
  utm_source: utmString,
  utm_term: utmString,
});

const signupRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: "/signup",
  component: SignUpPage,
  validateSearch: signupSearchSchema,
});

const signupSuccessRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: "/signup/success",
  component: ConfirmEmail,
});

const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify",
  component: lazyRouteComponent(
    () => import("pages/register/RegisterComplete"),
    "RegisterComplete",
  ),
  validateSearch: (search: { email?: string; hash?: string }) => search,
  // This prevents the RegisterComplete component
  // from re-rendering for authed users (e.g. a
  // user refreshes the page on the /verify route)
  beforeLoad: ({ context }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: "/projects" });
    }
  },
});

/* Success layout */

const successLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: "successLayout",
  component: SuccessLayout,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => successLayout,
  path: "/resetPassword",
  validateSearch: (search: { token: string | null }) => search,
  component: ResetPassword,
});

const verifyBillingRoute = createRoute({
  getParentRoute: () => successLayout,
  path: "/verifyBilling",
  component: lazyRouteComponent(
    () => import("pages/billing/billingEmails/BillingEmailVerification"),
    "BillingEmailVerification",
  ),
  validateSearch: (search: { beId: number; hash: string }) => search,
});

const idpErrorRoute = createRoute({
  getParentRoute: () => successLayout,
  path: "/idpError",
  component: lazyRouteComponent(
    () => import("pages/register/IDPError"),
    "IDPError",
  ),
  validateSearch: (search: { reason: IdpErrorReasons; operation?: string }) =>
    search,
  beforeLoad: ({ search }) => {
    if (!search.reason) {
      throw redirect({ to: "/" });
    }
  },
});

const publicErrorRoute = createRoute({
  getParentRoute: () => successLayout,
  path: "/404",
  component: lazyRouteComponent(
    () => import("pages/NotFoundPage"),
    "NotFoundPage",
  ),
});

const unsupportedBrowserRoute = createRoute({
  getParentRoute: () => successLayout,
  path: "/unsupported-browser",
  component: lazyRouteComponent(
    () => import("pages/UnsupportedBrowserPage"),
    "UnsupportedBrowserPage",
  ),
});

/* oAuth routes */

const successAuthLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: "successAuthLayout",
  component: SuccessLayout,
  beforeLoad: async (opts) => {
    await checkAuth(opts);
  },
});

const oAuthAuthorizationRoute = createRoute({
  getParentRoute: () => successAuthLayout,
  path: "/oauth/authorize",
  component: lazyRouteComponent(
    () => import("pages/auth/OAuthAuthorizationPage"),
    "OAuthAuthorizationPage",
  ),
  validateSearch: (search: {
    client_id: string | null;
    redirect_uri: string | null;
    response_type: string | null;
    code_challenge: string | null;
    code_challenge_method: string | null;
    state: string | null;
  }) => search,
});

const oAuthSuccessRoute = createRoute({
  getParentRoute: () => successAuthLayout,
  path: "/oauth/code/success",
  component: lazyRouteComponent(
    () => import("pages/auth/OAuthSuccessPage"),
    "OAuthSuccessPage",
  ),
});

/**
 * Where `tiger auth login --headless` sends the user. No parameters: a plain
 * URL keeps consent on this page.
 */
const oAuthDeviceRoute = createRoute({
  getParentRoute: () => successAuthLayout,
  path: "/oauth/device",
  component: lazyRouteComponent(
    () => import("pages/auth/OAuthDevicePage"),
    "OAuthDevicePage",
  ),
});

/* Auth layout */

const authLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: "authLayout",
  component: lazyRouteComponent(
    () => import("layouts/AuthLayout"),
    "AuthLayout",
  ),
  pendingComponent: DashboardShell,
  beforeLoad: (opts) => {
    checkAuthOnly(opts);
  },
  loader: async ({ context, location }) => {
    await whenAppReady();
    // `projectLayout` syncs the project in the URL.
    if (!getProjectIdFromPath(location.pathname)) {
      await syncSelectedProject(context.client);
    }
  },
});

/**
 * Layout for all project-scoped routes. Owns the `{$projectId}` URL param and
 * the project-dependent guards/loaders. Child routes inherit `projectId`, so
 * their own `path` is relative and never repeats `dashboard/{$projectId}`.
 *
 * NOTE: `/account` is declared as a static sibling of this route (see route
 * tree below) and — because TanStack ranks static segments above dynamic
 * params — always matches its own route rather than being interpreted as
 * `projectId = "account"`. Keep any new projectId-less top-level route as a
 * static sibling too.
 */
const projectLayout = createRoute({
  getParentRoute: () => authLayout,
  path: "projects/{$projectId}",
  pendingComponent: LoadingSpinner,
  // A spinner, unlike a skeleton, flashes when it shows only briefly. Keep the
  // router's default floor until this route gets a skeleton.
  pendingMinMs: 500,
  beforeLoad: async (opts) => {
    await syncAndValidateProject(opts);
    await checkAwaitingPlanGuard(opts);
  },
  // `beforeLoad` has already cached the project.
  loader: async ({ context, params }) => {
    await loadServices(context.client, params.projectId);
  },
});

/**
 * Static `/projects` entry route. Authenticated users are sent here (rather
 * than to a hardcoded project URL); it resolves the active projectId and
 * forwards to that project's services page. A user without a project stays
 * here, and the page creates one.
 */
const dashboardEntryRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "projects",
  component: lazyRouteComponent(
    () => import("pages/onboarding/FirstProjectPage"),
    "FirstProjectPage",
  ),
  beforeLoad: async ({ context }) => {
    await whenAppReady();
    const projectId = await resolveProjectId(context.client);
    if (!projectId) return;

    // A stored legacy redirect URL (e.g. `/projects/services/<id>/overview` from
    // a logged-out deep link) couldn't be upgraded earlier because the projectId
    // wasn't resolved yet (see `handleAuthenticatedAccess`). Now that it is,
    // insert it to preserve the deep path instead of dropping to the default
    // services page.
    const redirectUrl = sessionStorage.getItem(REDIRECT_URL);
    if (redirectUrl && isLegacyProjectUrl(redirectUrl)) {
      sessionStorage.removeItem(REDIRECT_URL);
      localStorage.removeItem(POST_VERIFY_REDIRECT_URL);
      throw redirect({ to: toProjectScopedUrl(redirectUrl, projectId) });
    }

    throw redirect({
      to: "/projects/{$projectId}/services",
      params: { projectId },
    });
  },
});

// Sections that live at the top level (not under `/projects`) because they
// aren't project-scoped. Old `/dashboard/<section>` links for these map to
// `/<section>`; everything else maps to `/projects/<projectId>/<section>`.
const TOP_LEVEL_SECTIONS = new Set(["account"]);

/**
 * Reads the user's last-selected projectId from the Apollo cache — the parsed
 * `getUser.uiState` blob. Returns undefined on a cache miss or if `uiState` is
 * absent / not a JSON object, so callers can fall back to localStorage.
 */
const readSelectedProjectIdFromCache = (
  client: RouterContext["client"],
): string | undefined =>
  parseSelectedProjectId(
    client.readQuery<GetUserQuery>({ query: GET_USER })?.getUser.uiState,
  );

/**
 * Resolves the user's last-selected projectId, preferring the server-synced
 * value from the GetUser Apollo cache and falling back to the localStorage copy.
 */
const resolveSelectedProjectId = (
  client: RouterContext["client"],
): string | undefined =>
  readSelectedProjectIdFromCache(client) ??
  getFromLocalStorage<{ selectedProjectId?: string }>(
    UI_STATE,
    LOCAL_STORAGE_USER,
    {},
  ).selectedProjectId;

const redirectLegacyDashboard = ({
  context,
  location,
}: {
  context: RouterContext;
  location: ParsedLocation;
}): never => {
  const { pathname, searchStr } = location;
  const [, , section] = pathname.split("/"); // ["", "dashboard", "<section>"]

  // Top-level sections aren't project-scoped: map `/dashboard/<section>` to
  // `/<section>` with no projectId.
  if (section && TOP_LEVEL_SECTIONS.has(section)) {
    throw redirect({
      to: pathname.replace(/^\/dashboard/, "") + searchStr,
      replace: true,
    });
  }

  // Everything else lives under `/projects/{$projectId}`. Rewrite the prefix,
  // then insert the active projectId so the URL matches the current route tree
  // (`/dashboard/services/...` -> `/projects/<projectId>/services/...`). Without
  // it the section segment would be parsed as the projectId and fail to match.
  // Prefer the server-synced value from the GetUser cache, fall back to the
  // localStorage copy.
  const rewrittenPathname = pathname.replace(/^\/dashboard/, "/projects");
  const rewritten = rewrittenPathname + searchStr;
  if (isLegacyProjectUrl(rewritten)) {
    // Logged out: store the legacy `/projects/<section>/...` target and go to
    // login. Don't inject a projectId / redirect to a project route now — that
    // route's `syncAndValidateProject` guard runs before auth is established and
    // would bounce the unauthenticated user to the default services page,
    // stripping the deep path. `handleAuthenticatedAccess` upgrades the stored
    // legacy URL with the projectId post-login, once validation can succeed.
    if (!context.auth.isAuthenticated) {
      handleUnauthenticatedAccess(rewrittenPathname, searchStr);
    }

    // Authenticated: insert the active projectId so the URL matches the route
    // tree (`/projects/services/...` -> `/projects/<projectId>/services/...`).
    // Prefer the server-synced value from the GetUser cache, fall back to the
    // localStorage copy, and if neither is available redirect to `/projects`,
    // which resolves a project itself.
    const projectId = resolveSelectedProjectId(context.client);
    throw redirect({
      to: projectId ? toProjectScopedUrl(rewritten, projectId) : "/projects",
      replace: true,
    });
  }

  throw redirect({
    to: rewritten,
    replace: true,
  });
};

const legacyDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "dashboard",
  beforeLoad: redirectLegacyDashboard,
});

const legacyDashboardSplatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "dashboard/$",
  beforeLoad: redirectLegacyDashboard,
});

/* For projects with a plan type of AWAITING_PLAN.
   They must select a plan before accessing the dashboard.
*/

const welcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/welcome",
  component: lazyRouteComponent(
    () => import("pages/onboarding/WelcomePage"),
    "WelcomePage",
  ),
  pendingComponent: LoadingSpinner,
  // See `projectLayout`: keep the spinner's floor until this gets a skeleton.
  pendingMinMs: 500,
  beforeLoad: async (opts) => {
    await checkAuth(opts);
  },
});

/* Create service routes */

const createServiceRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "create_services/{-$cloneType}/{-$serviceId}",
  component: lazyRouteComponent(
    () => import("pages/project/service/create/CreateServicePage"),
    "CreateServicePage",
  ),
  validateSearch: (search: { isFree?: boolean }) => search,
});

const createServiceCompleteRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "create_services/complete",
  component: lazyRouteComponent(
    () =>
      import("pages/project/service/create/CreateServiceComplete/CreateServiceComplete"),
    "CreateServiceComplete",
  ),
});

const serviceConfigRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "services/{$serviceId}/config",
  component: lazyRouteComponent(
    () => import("pages/project/service/envFlow/SaveConfigPage"),
    "SaveConfigPage",
  ),
});

/* Branch routes */

// The layout and the branch pages are separate lazy chunks. The layout waits
// behind the overview skeleton, so a cold load does not fall back to
// `projectLayout`'s spinner, and the section layouts add a skeleton of their
// own while their pages load.
const branchLayout = createRoute({
  getParentRoute: () => projectLayout,
  path: "services/{$serviceId}/branches/{$branchId}",
  pendingComponent: BranchOverviewSkeleton,
  component: lazyRouteComponent(
    () => import("pages/project/branch/BranchLayout"),
    "BranchLayout",
  ),
});

const branchRedirectRoute = createRoute({
  getParentRoute: () => branchLayout,
  path: "/",
  loader: ({ params }) => {
    throw redirect({
      to: "/projects/{$projectId}/services/{$serviceId}/branches/{$branchId}/overview",
      params: {
        projectId: params.projectId,
        serviceId: params.serviceId,
        branchId: params.branchId,
      },
    });
  },
});

const branchOverviewRoute = createRoute({
  getParentRoute: () => branchLayout,
  path: "/overview",
  pendingComponent: BranchOverviewSkeleton,
  component: lazyRouteComponent(
    () => import("pages/project/branch/BranchOverview"),
    "BranchOverview",
  ),
});

const branchOperationsRoute = createRoute({
  getParentRoute: () => branchLayout,
  path: "/operations",
  pendingComponent: BranchOverviewSkeleton,
  component: lazyRouteComponent(
    () => import("pages/project/branch/BranchOperationsLayout"),
    "BranchOperationsLayout",
  ),
});

// The area opens on its first section, so `/operations` on its own is a place
// to land rather than an empty column.
const branchOperationsRedirectRoute = createRoute({
  getParentRoute: () => branchOperationsRoute,
  path: "/",
  loader: ({ params }) => {
    throw redirect({
      to: "/projects/{$projectId}/services/{$serviceId}/branches/{$branchId}/operations/resources",
      params: {
        projectId: params.projectId,
        serviceId: params.serviceId,
        branchId: params.branchId,
      },
    });
  },
});

// The service's Compute and storage page, reading the branch through a
// `ServiceIdOverrideProvider`.
const branchOperationsResourcesRoute = createRoute({
  getParentRoute: () => branchOperationsRoute,
  path: "/resources",
  component: lazyRouteComponent(
    () => import("pages/project/branch/BranchResources"),
    "BranchResources",
  ),
});

const branchOperationsManagementRoute = createRoute({
  getParentRoute: () => branchOperationsRoute,
  path: "/branch-management",
  component: lazyRouteComponent(
    () => import("pages/project/branch/BranchOperations"),
    "BranchOperations",
  ),
});

/*
 * The Explorer inside the branch area. It renders the same pages as the service
 * Explorer (see `explorerRoutes`) and adds no legacy redirects: `/storage`,
 * `/columnstore`, `/data-tiering` and `/cagg` exist for saved service links
 * only. The branch Explorer layout makes the Explorer read the branch, and keeps
 * its links in this area.
 */
const branchExplorerLayout = createRoute({
  getParentRoute: () => branchLayout,
  path: "/explorer",
  // The layout waits for the branch and for its own chunk, so it shows the
  // Explorer skeleton instead of the overview one above it.
  pendingComponent: ExplorerRouteSkeleton,
  beforeLoad: ({ location, params }) =>
    redirectIfAtExplorerRoot(location, params),
  component: lazyRouteComponent(
    () => import("pages/project/branch/BranchExplorerLayout"),
    "BranchExplorerLayout",
  ),
});

/**
 * The Explorer's two layouts. They do different jobs: the service one (declared
 * below) redirects the bare path and renders `ExplorerPage`, the branch one
 * provides the service id override and its own data gate. The pages under them
 * are the same, so they are declared once, here, and the area picks the parent.
 */
type ExplorerLayout =
  | typeof branchExplorerLayout
  | typeof serviceExplorerLayout;

const explorerRoutes = (getParentRoute: () => ExplorerLayout) => ({
  general: createRoute({
    getParentRoute,
    path: "/general",
    validateSearch: (search: ExplorerGeneralSearch) => search,
    component: lazyRouteComponent(
      () => import("pages/project/service/explorer/home/ExplorerHomePage"),
      "ExplorerHomePage",
    ),
  }),
  query: createRoute({
    getParentRoute,
    path: "/query/{$queryId}",
    component: lazyRouteComponent(
      () => import("pages/project/service/explorer/query/ExplorerQueryPage"),
      "ExplorerQueryPage",
    ),
  }),
  object: createRoute({
    getParentRoute,
    path: "/object/{$schema}/{$objectType}/{$objectName}",
    validateSearch: (search: ExplorerObjectSearch) => search,
    component: lazyRouteComponent(
      () => import("pages/project/service/explorer/tableInfo/TableInfoPage"),
      "TableInfoPage",
    ),
  }),
});

const branchExplorerRoutes = explorerRoutes(() => branchExplorerLayout);

const serviceConnectRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "services/{$serviceId}/connect/{-$connectType}",
  component: lazyRouteComponent(
    () =>
      import("pages/project/service/envFlow/ConnectServicePage/ConnectServicePage"),
    "ConnectServicePage",
  ),
});

const serviceImportDataRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "services/{$serviceId}/import/{-$importType}",
  component: lazyRouteComponent(
    () => import("pages/project/service/envFlow/ImportDataPage/ImportDataPage"),
    "ImportDataPage",
  ),
});

const serviceImportDataSuccessRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "services/{$serviceId}/importSuccess",
  component: lazyRouteComponent(
    () => import("pages/project/service/envFlow/DataImportSuccessPage"),
    "DataImportSuccessPage",
  ),
});

const serviceS3ImportRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "services/{$serviceId}/import/s3-import",
  component: lazyRouteComponent(
    () => import("pages/project/service/envFlow/S3ImportPage/S3ImportPage"),
    "S3ImportPage",
  ),
});

const serviceSingleFileUploadRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "services/{$serviceId}/import/file/{-$sourceType}",
  component: lazyRouteComponent(
    () =>
      import("pages/project/service/envFlow/ImportDataPage/SingleFileImport"),
    "SingleFileImport",
  ),
});

const serviceS3LiveSyncImportRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: `services/{$serviceId}/s3-live-sync/{-$liveSyncType}/{-$resourceId}`,
  component: lazyRouteComponent(
    () => import("pages/project/service/connectors/S3ConnectorPage"),
    "S3ConnectorPage",
  ),
});

/* Project routes */

const projectRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "services",
  component: lazyRouteComponent(
    () => import("pages/project/ProjectPage"),
    "ProjectPage",
  ),
  validateSearch: (search: { popsql?: string | null }) => search,
});

const joinProjectRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "joinProject",
  component: LoadingSpinner,
  validateSearch: (search: { id?: string; hash?: string }) => search,
  beforeLoad: async ({ context, search }) => {
    const { id, hash } = search;

    let joinedProjectId: string | undefined;

    if (id && hash) {
      try {
        const result = await context.client.mutate<
          JoinProjectMutation,
          JoinProjectMutationVariables
        >({
          mutation: JOIN_PROJECT,
          variables: { inviteId: id.toString(), hash },
        });

        joinedProjectId = showJoinProjectResult(
          result,
          "Project invite accepted!",
          notificationStore,
        );

        context.client.cache.evict({
          fieldName: "getAllProjects",
        });
        context.client.cache.gc();
      } catch (e) {
        console.error("Failed to accept project invitation", e);
      }
    }

    if (joinedProjectId) {
      throw redirect({
        to: "/projects/{$projectId}/services",
        params: { projectId: joinedProjectId },
      });
    }

    throw redirect({ to: "/projects" });
  },
});

const projectSettingsRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "settings",
  component: lazyRouteComponent(
    () => import("pages/project/settings/ProjectSettings"),
    "ProjectSettings",
  ),
  validateSearch: (search: {
    tabId?: string;
    serviceId?: string;
    from?: string;
  }) => search,
});

const exitSurveyRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "no_services",
  component: lazyRouteComponent(
    () => import("pages/project/ExitSurveyPage"),
    "ExitSurveyPage",
  ),
  validateSearch: (search: {
    deletedAllServices?: boolean;
    serviceEnvironment?: ServiceEnvironment;
  }) => search,
});

/* Billing routes */

const billingLayout = createRoute({
  getParentRoute: () => projectLayout,
  path: "billing",
  component: lazyRouteComponent(
    () => import("pages/billing/BillingPage"),
    "BillingPage",
  ),
  validateSearch: (search: { addPayment?: boolean }) => search,
});

const billingRedirectRoute = createRoute({
  getParentRoute: () => billingLayout,
  path: "/",
  loader: ({ params }) => {
    throw redirect({
      to: "/projects/{$projectId}/billing/details",
      params: { projectId: params.projectId },
    });
  },
});

const billingDetailsRoute = createRoute({
  getParentRoute: () => billingLayout,
  path: "/details",
  component: lazyRouteComponent(
    () => import("pages/billing/billingDetails/BillingDetails"),
    "BillingDetails",
  ),
});

const billingPaymentsRoute = createRoute({
  getParentRoute: () => billingLayout,
  path: "/payments",
  component: lazyRouteComponent(
    () => import("pages/billing/billingHistory/BillingHistory"),
    "BillingInvoices",
  ),
});

const billingEmailsRoute = createRoute({
  getParentRoute: () => billingLayout,
  path: "/emails",
  component: lazyRouteComponent(
    () => import("pages/billing/billingEmails/BillingEmails"),
    "BillingEmails",
  ),
});

const billingPlansRoute = createRoute({
  getParentRoute: () => billingLayout,
  path: "/plans",
  component: lazyRouteComponent(
    () => import("pages/billing/billingPlans/BillingPlans"),
    "BillingPlans",
  ),
  validateSearch: (search: {
    provider?: "aws" | "azure";
    downgradePlan?: VirtualPlanType;
    upgradePlan?: VirtualPlanType;
    convertService?: string;
  }): {
    provider?: "aws" | "azure";
    downgradePlan?: VirtualPlanType;
    upgradePlan?: VirtualPlanType;
    convertService?: string;
  } => ({
    ...search,
    // VirtualPlanType values are uppercase.
    // See VirtualPlanType in generated.ts
    ...(search.downgradePlan && {
      downgradePlan: search.downgradePlan.toUpperCase() as VirtualPlanType,
    }),
    ...(search.upgradePlan && {
      upgradePlan: search.upgradePlan.toUpperCase() as VirtualPlanType,
    }),
    ...(search.convertService && {
      convertService: search.convertService,
    }),
  }),
});

const billingAddonsRoute = createRoute({
  getParentRoute: () => billingLayout,
  path: "/addons",
  component: lazyRouteComponent(
    () => import("pages/billing/billingAddons/BillingAddons"),
    "BillingAddons",
  ),
});

const billingTimelineRoute = createRoute({
  getParentRoute: () => billingLayout,
  path: "/timeline",
  component: lazyRouteComponent(
    () => import("pages/billing/billingTimeline/BillingTimeline"),
    "BillingTimeline",
  ),
});

/* Service routes */

const serviceLayout = createRoute({
  getParentRoute: () => projectLayout,
  path: "services/{$serviceId}",
  component: lazyRouteComponent(
    () => import("pages/project/service/ServicePage"),
    "ServicePage",
  ),
});

const serviceRedirectRoute = createRoute({
  getParentRoute: () => serviceLayout,
  path: "/",
  loader: ({ params }) => {
    throw redirect({
      to: "/projects/{$projectId}/services/{$serviceId}/overview",
      params: { projectId: params.projectId, serviceId: params.serviceId },
    });
  },
});

/* Service sub-routes */

const serviceOverviewRoute = createRoute({
  getParentRoute: () => serviceLayout,
  path: "/overview",
  component: lazyRouteComponent(
    () => import("pages/project/service/overview/ServiceOverviewPage"),
    "ServiceOverviewPage",
  ),
});

/* Service actions routes */

const serviceActionsLayout = createRoute({
  getParentRoute: () => serviceLayout,
  path: "/actions",
  component: lazyRouteComponent(
    () => import("pages/project/service/actions/ActionsPage"),
    "ActionsPage",
  ),
});

const serviceActionsGoalsRoute = createRoute({
  getParentRoute: () => serviceActionsLayout,
  path: "/goals",
  component: lazyRouteComponent(
    () => import("pages/project/service/actions/GoalsPage"),
    "GoalsPage",
  ),
  validateSearch: (search: { wizard?: string }) => search,
});

const serviceActionsConnectRoute = createRoute({
  getParentRoute: () => serviceActionsLayout,
  path: "/connect/{-$connectType}",
  component: lazyRouteComponent(
    () =>
      import("pages/project/service/envFlow/ConnectServicePage/ConnectServicePage"),
    "ConnectServicePage",
  ),
});

const serviceActionsImportDataRoute = createRoute({
  getParentRoute: () => serviceActionsLayout,
  path: "/import/{-$importType}",
  component: lazyRouteComponent(
    () => import("pages/project/service/envFlow/ImportDataPage/ImportDataPage"),
    "ImportDataPage",
  ),
});

const serviceActionsSingleFileUploadRoute = createRoute({
  getParentRoute: () => serviceActionsLayout,
  path: "/import/file/{-$sourceType}",
  component: lazyRouteComponent(
    () =>
      import("pages/project/service/envFlow/ImportDataPage/SingleFileImport"),
    "SingleFileImport",
  ),
});

const serviceActionsS3ImportRoute = createRoute({
  getParentRoute: () => serviceActionsLayout,
  path: "/import/s3-import",
  component: lazyRouteComponent(
    () => import("pages/project/service/envFlow/S3ImportPage/S3ImportPage"),
    "S3ImportPage",
  ),
  validateSearch: (search: { syncId?: string; fromOnboarding?: boolean }) =>
    search,
  beforeLoad: checkFreeServiceGuard,
});

const serviceActionsS3LiveSyncRoute = createRoute({
  getParentRoute: () => serviceActionsLayout,
  path: "/s3-live-sync/{-$liveSyncType}/{-$resourceId}",
  component: lazyRouteComponent(
    () => import("pages/project/service/connectors/S3ConnectorPage"),
    "S3ConnectorPage",
  ),
  beforeLoad: checkFreeServiceGuard,
});

/* Service explorer routes */

/**
 * The layout redirects the bare `/explorer` path, not an index redirect route:
 * with this route tree, the layout ranks above its own index child in the
 * router's match order, so a child with `path: "/"` never matches.
 */
const serviceExplorerLayout = createRoute({
  getParentRoute: () => serviceLayout,
  path: "/explorer",
  beforeLoad: ({ location, params }) =>
    redirectIfAtExplorerRoot(location, params),
  component: lazyRouteComponent(
    () => import("pages/project/service/explorer/ExplorerPage"),
    "ExplorerPage",
  ),
});

const serviceExplorerRoutes = explorerRoutes(() => serviceExplorerLayout);

/*
 * Earlier explorer pages that are now tables on the home tab. The console
 * links to the home tab, but saved links and links from outside the console
 * can still use these paths.
 */
const redirectToExplorerHome = (
  params: { projectId: string; serviceId: string },
  tab?: ExplorerHomeTab,
): never => {
  throw redirect({
    to: "/projects/{$projectId}/services/{$serviceId}/explorer/general",
    params: { projectId: params.projectId, serviceId: params.serviceId },
    search: tab ? { tab } : {},
    replace: true,
  });
};

const serviceExplorerStorageRoute = createRoute({
  getParentRoute: () => serviceExplorerLayout,
  path: "/storage",
  beforeLoad: ({ params }) => redirectToExplorerHome(params, "columnstore"),
});

const serviceExplorerColumnstoreRoute = createRoute({
  getParentRoute: () => serviceExplorerLayout,
  path: "/columnstore",
  beforeLoad: ({ params }) => redirectToExplorerHome(params, "columnstore"),
});

const serviceExplorerDataTieringRoute = createRoute({
  getParentRoute: () => serviceExplorerLayout,
  path: "/data-tiering",
  beforeLoad: ({ params }) => redirectToExplorerHome(params),
});

const serviceExplorerCaggRoute = createRoute({
  getParentRoute: () => serviceExplorerLayout,
  path: "/cagg",
  validateSearch: (search: { tabId?: "cagg" | "hypertable" }) => search,
  beforeLoad: ({ params, search }) =>
    redirectToExplorerHome(
      params,
      search.tabId === "hypertable" ? "hypertables" : "continuous-aggregates",
    ),
});

/* Service monitoring routes */

const serviceMonitoringLayout = createRoute({
  getParentRoute: () => serviceLayout,
  path: "/monitoring",
  component: lazyRouteComponent(
    () => import("pages/project/service/monitoring/MonitoringPage"),
    "MonitoringPage",
  ),
});

const serviceMonitoringMetricsRoute = createRoute({
  getParentRoute: () => serviceMonitoringLayout,
  path: "/metrics",
  component: lazyRouteComponent(
    () => import("pages/project/service/monitoring/metrics/MetricsPage"),
    "MetricsPage",
  ),
  validateSearch: (search: { role?: InstanceRole }) => search,
});

const serviceMonitoringLogsRoute = createRoute({
  getParentRoute: () => serviceMonitoringLayout,
  path: "/logs",
  component: lazyRouteComponent(
    () => import("pages/project/service/monitoring/logs/LogsPage"),
    "LogsPage",
  ),
  validateSearch: (search: ServiceLogsSearch) => search,
});

const serviceMonitoringJobsRoute = createRoute({
  getParentRoute: () => serviceMonitoringLayout,
  path: "/jobs",
  component: lazyRouteComponent(
    () => import("pages/project/service/monitoring/jobs/JobsPage"),
    "JobsPage",
  ),
  validateSearch: (search: { tab?: "table" | "timeline" }) => search,
});

const serviceMonitoringJobDetailRoute = createRoute({
  getParentRoute: () => serviceMonitoringLayout,
  path: "/jobs/{$jobId}",
  component: lazyRouteComponent(
    () => import("pages/project/service/monitoring/jobs/JobDetailPage"),
    "JobDetailPage",
  ),
});

const serviceMonitoringInsightsRoute = createRoute({
  getParentRoute: () => serviceMonitoringLayout,
  path: "/insights",
  component: lazyRouteComponent(
    () => import("pages/project/service/monitoring/insights/QueryStatsPage"),
    "QueryStatsPage",
  ),
  validateSearch: (search: { role?: InstanceRole }) => search,
});

const serviceMonitoringQueryDetailsRoute = createRoute({
  getParentRoute: () => serviceMonitoringLayout,
  path: "/insights/{$queryHash}",
  component: lazyRouteComponent(
    () => import("pages/project/service/monitoring/insights/QueryDetailsPage"),
    "QueryDetailsPage",
  ),
  validateSearch: (search: { role?: InstanceRole }) => search,
});

const serviceMonitoringConnectionsRoute = createRoute({
  getParentRoute: () => serviceMonitoringLayout,
  path: "/connections",
  component: lazyRouteComponent(
    () =>
      import("pages/project/service/monitoring/connections/ConnectionsPage"),
    "ConnectionsPage",
  ),
});

const serviceMonitoringRecommendationsRoute = createRoute({
  getParentRoute: () => serviceMonitoringLayout,
  path: "/recommendations",
  component: lazyRouteComponent(
    () =>
      import("pages/project/service/monitoring/recommendations/RecommendationsPage"),
    "RecommendationsPage",
  ),
});

/* Service connectors routes */

const serviceConnectorsLayout = createRoute({
  getParentRoute: () => serviceLayout,
  path: "/connectors",
  component: lazyRouteComponent(
    () => import("pages/project/service/connectors/ConnectorsPage"),
    "ConnectorsPage",
  ),
  beforeLoad: checkFreeServiceGuard,
});

const serviceConnectorsOverviewRoute = createRoute({
  getParentRoute: () => serviceConnectorsLayout,
  path: "/overview",
  component: lazyRouteComponent(
    () => import("pages/project/service/connectors/ConnectorsOverviewPage"),
    "ConnectorsOverviewPage",
  ),
});

const serviceConnectorsSourceRoute = createRoute({
  getParentRoute: () => serviceConnectorsLayout,
  path: "/source",
  component: lazyRouteComponent(
    () => import("pages/project/service/connectors/SourceConnectorsPage"),
    "SourceConnectorsPage",
  ),
  validateSearch: (search: { postgresReplicationSlotName?: string }) => search,
});

const serviceConnectorsDestinationRoute = createRoute({
  getParentRoute: () => serviceConnectorsLayout,
  path: "/destination",
  component: lazyRouteComponent(
    () => import("pages/project/service/connectors/DestinationConnectorsPage"),
    "DestinationConnectorsPage",
  ),
});

const serviceConnectorsLiveSyncRoute = createRoute({
  getParentRoute: () => serviceConnectorsLayout,
  path: "/postgres/{-$liveSyncType}/{-$resourceId}",
  component: lazyRouteComponent(
    () => import("pages/project/service/connectors/PostgresConnectorPage"),
    "PostgresConnectorPage",
  ),
});

const serviceConnectorsS3LiveSyncRoute = createRoute({
  getParentRoute: () => serviceConnectorsLayout,
  path: "/s3/{-$liveSyncType}/{-$resourceId}",
  component: lazyRouteComponent(
    () => import("pages/project/service/connectors/S3ConnectorPage"),
    "S3ConnectorPage",
  ),
  validateSearch: (search: { fromOnboarding?: boolean }) => search,
});

const serviceConnectorsIcebergRoute = createRoute({
  getParentRoute: () => serviceConnectorsLayout,
  path: "/iceberg/{-$liveSyncType}/{-$resourceId}",
  component: lazyRouteComponent(
    () => import("pages/project/service/connectors/IcebergConnectorPage"),
    "IcebergConnectorPage",
  ),
});

const serviceConnectorsKafkaRoute = createRoute({
  getParentRoute: () => serviceConnectorsLayout,
  path: "/kafka/{-$liveSyncType}/{-$resourceId}",
  component: lazyRouteComponent(
    () => import("pages/project/service/connectors/KafkaConnectorPage"),
    "KafkaConnectorPage",
  ),
});

/* Service branches routes */

// The list of the branches of a service. The pages of one branch are
// `branchLayout`, a project route: "active" is a static segment, so it ranks
// above that route's `{$branchId}`.
const serviceBranchesLayout = createRoute({
  getParentRoute: () => serviceLayout,
  path: "/branches",
  component: lazyRouteComponent(
    () => import("pages/project/service/branches/BranchesLayout"),
    "BranchesLayout",
  ),
});

const serviceBranchesRedirectRoute = createRoute({
  getParentRoute: () => serviceBranchesLayout,
  path: "/",
  loader: ({ params }) => {
    throw redirect({
      to: "/projects/{$projectId}/services/{$serviceId}/branches/active",
      params: { projectId: params.projectId, serviceId: params.serviceId },
    });
  },
});

const serviceBranchesActiveRoute = createRoute({
  getParentRoute: () => serviceBranchesLayout,
  path: "/active",
  component: lazyRouteComponent(
    () => import("pages/project/service/branches/BranchesPage"),
    "BranchesPage",
  ),
});

/* Service SQL editor route */

const serviceSqlEditorRoute = createRoute({
  getParentRoute: () => serviceLayout,
  path: "/sql_editor",
  component: lazyRouteComponent(
    () => import("pages/project/service/sqlEditor/SqlEditorPage"),
    "SqlEditorPage",
  ),
  validateSearch: (search: { tableName?: string; schemaName?: string }) =>
    search,
});

/* Service operations routes */

const serviceOperationsLayout = createRoute({
  getParentRoute: () => serviceLayout,
  path: "/operations",
  component: lazyRouteComponent(
    () => import("pages/project/service/operations/OperationsPage"),
    "OperationsPage",
  ),
});

const serviceOperationsResourcesRoute = createRoute({
  getParentRoute: () => serviceOperationsLayout,
  path: "/resources",
  component: lazyRouteComponent(
    () =>
      import("pages/project/service/operations/resources/OperationsComputePage"),
    "OperationsComputePage",
  ),
});

const serviceOperationsHighAvailabilityRoute = createRoute({
  getParentRoute: () => serviceOperationsLayout,
  path: "/high-availability",
  component: lazyRouteComponent(
    () =>
      import("pages/project/service/operations/highAvailability/HighAvailabilityPage"),
    "HighAvailabilityPage",
  ),
  validateSearch: (search: { tabIndex?: number }) => search,
  beforeLoad: checkFreeServiceGuard,
});

const serviceOperationsReadReplicasRoute = createRoute({
  getParentRoute: () => serviceOperationsLayout,
  path: "/read-replicas",
  component: lazyRouteComponent(
    () =>
      import("pages/project/service/operations/readReplicas/ReadReplicasPage"),
    "ReadReplicasPage",
  ),
  beforeLoad: checkFreeServiceGuard,
});

const serviceOperationsBackupRestoreRoute = createRoute({
  getParentRoute: () => serviceOperationsLayout,
  path: "/backup-restore",
  component: lazyRouteComponent(
    () =>
      import("pages/project/service/operations/OperationsBackupRestorePage"),
    "OperationsBackupRestorePage",
  ),
  beforeLoad: checkFreeServiceGuard,
});

const serviceOperationsExtensionsRoute = createRoute({
  getParentRoute: () => serviceOperationsLayout,
  path: "/extensions",
  component: lazyRouteComponent(
    () => import("pages/project/service/operations/ExtensionsPage"),
    "ExtensionsPage",
  ),
});

const serviceOperationsConnectionPoolingRoute = createRoute({
  getParentRoute: () => serviceOperationsLayout,
  path: "/connection-pooling",
  component: lazyRouteComponent(
    () => import("pages/project/service/operations/ConnectionPoolingPage"),
    "ConnectionPoolingPage",
  ),
  beforeLoad: checkFreeServiceGuard,
});

const serviceOperationsEnvironmentRoute = createRoute({
  getParentRoute: () => serviceOperationsLayout,
  path: "/environment",
  component: lazyRouteComponent(
    () => import("pages/project/service/operations/EnvironmentPage"),
    "EnvironmentPage",
  ),
  beforeLoad: checkFreeServiceGuard,
});

const serviceOperationsUpgradesRoute = createRoute({
  getParentRoute: () => serviceOperationsLayout,
  path: "/upgrades",
  component: lazyRouteComponent(
    () => import("pages/project/service/operations/ServiceUpgradesPage"),
    "ServiceUpgradesPage",
  ),
});

const serviceOperationsSecurityRoute = createRoute({
  getParentRoute: () => serviceOperationsLayout,
  path: "/security",
  validateSearch: (search: {
    tabId?: "vpc" | "privateEndpoints" | "ipAllowList" | "passwordlessAccess";
  }) => search,
  component: lazyRouteComponent(
    () =>
      import("pages/project/service/operations/security/OperationsSecurityPage"),
    "OperationsSecurityPage",
  ),
});

const serviceOperationsIntegrationsRoute = createRoute({
  getParentRoute: () => serviceOperationsLayout,
  path: "/integrations",
  component: lazyRouteComponent(
    () =>
      import("pages/project/service/operations/integrations/OperationsIntegrationsPage"),
    "OperationsIntegrationsPage",
  ),
  beforeLoad: checkFreeServiceGuard,
});

const serviceOperationsDatabaseParametersRoute = createRoute({
  getParentRoute: () => serviceOperationsLayout,
  path: "/database_parameters",
  component: lazyRouteComponent(
    () =>
      import("pages/project/service/operations/databaseParameters/OperationsDatabaseParametersPage"),
    "OperationsDatabaseParametersPage",
  ),
});

const serviceOperationsManagementRoute = createRoute({
  getParentRoute: () => serviceOperationsLayout,
  path: "/management/{-$action}",
  component: lazyRouteComponent(
    () => import("pages/project/service/operations/ServiceManagement"),
    "ServiceManagement",
  ),
  validateSearch: (search: { redirectRecoveryForkId?: string }) => search,
});

/* Security route */

const securityRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "security",
  component: lazyRouteComponent(
    () => import("pages/security/SecurityPage"),
    "SecurityPage",
  ),
});

const securityRedirectRoute = createRoute({
  getParentRoute: () => securityRoute,
  path: "/",
  validateSearch: (search: { tabId?: "vpc" | "ipAllowList" }) => search,
  beforeLoad: ({ params, search }) => {
    const { projectId } = params;
    throw redirect(
      search.tabId === "ipAllowList"
        ? {
            to: "/projects/{$projectId}/security/ipAllowList",
            params: { projectId },
          }
        : { to: "/projects/{$projectId}/security/vpc", params: { projectId } },
    );
  },
});

const securityVpcRoute = createRoute({
  getParentRoute: () => securityRoute,
  path: "/vpc",
  component: lazyRouteComponent(
    () => import("pages/security/vpc/VpcPage"),
    "VpcPage",
  ),
});

const securityPrivateEndpointsRoute = createRoute({
  getParentRoute: () => securityRoute,
  path: "/private-endpoints",
  component: lazyRouteComponent(
    () => import("pages/security/privateEndpoints/PrivateEndpointsPage"),
    "PrivateEndpointsPage",
  ),
});

const securityIpAllowListRoute = createRoute({
  getParentRoute: () => securityRoute,
  path: "/ipAllowList",
  component: lazyRouteComponent(
    () => import("pages/security/ipAllowLists/IpAllowListPage"),
    "IpAllowListPage",
  ),
});

const securityAuthenticationRoute = createRoute({
  getParentRoute: () => securityRoute,
  path: "/authentication",
  component: lazyRouteComponent(
    () => import("pages/security/authentication/AuthenticationPage"),
    "AuthenticationPage",
  ),
});

/* Exporters route */

const integrationsRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "integrations",
  component: lazyRouteComponent(
    () => import("pages/integrations/IntegrationsPage"),
    "IntegrationsPage",
  ),
});

/* Activity route */

const activityRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "activity",
  component: lazyRouteComponent(
    () => import("pages/activity/ActivityPage"),
    "ActivityPage",
  ),
});

/* Members route */

const membersRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "members",
  component: lazyRouteComponent(
    () => import("pages/members/MembersPage"),
    "MembersPage",
  ),
});

/* Data view route */

const dataViewRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "data-view",
  // The parent guard updates the Statsig user for this project first, so the
  // gate value is correct here.
  beforeLoad: ({ params }) => {
    if (isGateEnabled(FEAT_HIDE_DATA_VIEW)) {
      throw redirect({
        to: "/projects/{$projectId}/services",
        params: { projectId: params.projectId },
      });
    }
  },
  component: lazyRouteComponent(
    () => import("pages/project/DataViewPage"),
    "DataViewPage",
  ),
});

/* Play route: local scratch page for trying out components */

const playRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "play/{-$featureId}",
  component: lazyRouteComponent(() => import("pages/Play"), "Play"),
});

/* Support routes */

const supportRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "/support",
  component: lazyRouteComponent(
    () => import("pages/support/SupportPage"),
    "SupportPage",
  ),
});

const supportRedirectRoute = createRoute({
  getParentRoute: () => supportRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    const { projectId } = params;
    throw redirect({
      to: "/projects/{$projectId}/support/main",
      params: { projectId },
    });
  },
});

const supportMainRoute = createRoute({
  getParentRoute: () => supportRoute,
  path: "/main",
  component: lazyRouteComponent(
    () => import("pages/support/SupportMainPage"),
    "SupportMainPage",
  ),
});

const supportCasesRoute = createRoute({
  getParentRoute: () => supportRoute,
  path: "/cases/{-$caseId}",
  component: lazyRouteComponent(
    () => import("pages/support/SupportCasesPage"),
    "SupportCasesPage",
  ),
});

const supportEmailRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "/support/email",
  component: lazyRouteComponent(
    () => import("pages/support/EmailSupportPage"),
    "EmailSupportPage",
  ),
  validateSearch: (search: {
    serviceId?: string;
    serviceRegionCode?: string;
    subject?: string;
    description?: string;
    capability?: SupportServiceCapability;
    caseType?: SupportCaseType;
    billingCategory?: SupportBillingCategory;
  }) => search,
});

const accountSettingsRoute = createRoute({
  getParentRoute: () => authLayout,
  path: "/account",
  component: lazyRouteComponent(
    () => import("pages/account/AccountSettings"),
    "AccountSettings",
  ),
});

/* CLI & MCP route */

const cliMcpLayout = createRoute({
  getParentRoute: () => projectLayout,
  path: "/cli-mcp",
  component: lazyRouteComponent(
    () => import("pages/cliMcp/CliMcpPage"),
    "CliMcpPage",
  ),
});

const cliMcpRedirectRoute = createRoute({
  getParentRoute: () => cliMcpLayout,
  path: "/",
  beforeLoad: ({ params }) => {
    const { projectId } = params;
    throw redirect({
      to: "/projects/{$projectId}/cli-mcp/cli",
      params: { projectId },
    });
  },
});

const cliMcpCLIRoute = createRoute({
  getParentRoute: () => cliMcpLayout,
  path: "/cli",
  component: lazyRouteComponent(
    () => import("pages/cliMcp/CLIPage"),
    "CLIPage",
  ),
});

const cliMcpMCPRoute = createRoute({
  getParentRoute: () => cliMcpLayout,
  path: "/mcp",
  component: lazyRouteComponent(
    () => import("pages/cliMcp/MCPServerPage"),
    "MCPServerPage",
  ),
});

/* Error routes */

const errorRoute = createRoute({
  getParentRoute: () => projectLayout,
  path: "404",
  component: lazyRouteComponent(
    () => import("pages/NotFoundPage"),
    "NotFoundPage",
  ),
});

// Not found route
const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/$",
  beforeLoad: () => {
    throw redirect({ to: "/" });
  },
});

// NOTE: If you have a layout with a redirect
// route and it won't load (it shows the Service
// layout but with blank contents), try moving it
// higher up in the route tree.

const routeTree = rootRoute.addChildren([
  accountDeletionResultRoute,
  authLayout.addChildren([
    // Static `/projects` entry: resolves the active projectId and forwards.
    dashboardEntryRoute,
    // Project-scoped routes — all carry the `{$projectId}` param via projectLayout.
    projectLayout.addChildren([
      createServiceRoute,
      createServiceCompleteRoute,
      serviceConfigRoute,
      serviceConnectRoute,
      serviceSingleFileUploadRoute,
      serviceImportDataRoute,
      serviceImportDataSuccessRoute,
      serviceS3ImportRoute,
      serviceS3LiveSyncImportRoute,
      projectRoute,
      projectSettingsRoute,
      exitSurveyRoute,
      billingLayout.addChildren([
        billingRedirectRoute,
        billingDetailsRoute,
        billingPaymentsRoute,
        billingEmailsRoute,
        billingPlansRoute,
        billingAddonsRoute,
        billingTimelineRoute,
      ]),
      serviceLayout.addChildren([
        serviceRedirectRoute,
        serviceOverviewRoute,
        serviceActionsLayout.addChildren([
          serviceActionsGoalsRoute,
          serviceActionsConnectRoute,
          serviceActionsSingleFileUploadRoute,
          serviceActionsImportDataRoute,
          serviceActionsS3ImportRoute,
          serviceActionsS3LiveSyncRoute,
        ]),
        serviceExplorerLayout.addChildren([
          serviceExplorerRoutes.general,
          serviceExplorerRoutes.query,
          serviceExplorerStorageRoute,
          serviceExplorerColumnstoreRoute,
          serviceExplorerDataTieringRoute,
          serviceExplorerCaggRoute,
          serviceExplorerRoutes.object,
        ]),
        serviceMonitoringLayout.addChildren([
          serviceMonitoringMetricsRoute,
          serviceMonitoringLogsRoute,
          serviceMonitoringJobsRoute,
          serviceMonitoringJobDetailRoute,
          serviceMonitoringInsightsRoute,
          serviceMonitoringQueryDetailsRoute,
          serviceMonitoringConnectionsRoute,
          serviceMonitoringRecommendationsRoute,
        ]),
        serviceConnectorsLayout.addChildren([
          serviceConnectorsOverviewRoute,
          serviceConnectorsSourceRoute,
          serviceConnectorsDestinationRoute,
          serviceConnectorsLiveSyncRoute,
          serviceConnectorsS3LiveSyncRoute,
          serviceConnectorsIcebergRoute,
          serviceConnectorsKafkaRoute,
        ]),
        serviceSqlEditorRoute,
        serviceBranchesLayout.addChildren([
          serviceBranchesRedirectRoute,
          serviceBranchesActiveRoute,
        ]),
        serviceOperationsLayout.addChildren([
          serviceOperationsResourcesRoute,
          serviceOperationsHighAvailabilityRoute,
          serviceOperationsReadReplicasRoute,
          serviceOperationsBackupRestoreRoute,
          serviceOperationsExtensionsRoute,
          serviceOperationsConnectionPoolingRoute,
          serviceOperationsEnvironmentRoute,
          serviceOperationsUpgradesRoute,
          serviceOperationsSecurityRoute,
          serviceOperationsIntegrationsRoute,
          serviceOperationsDatabaseParametersRoute,
          serviceOperationsManagementRoute,
        ]),
      ]),
      branchLayout.addChildren([
        branchRedirectRoute,
        branchOverviewRoute,
        branchOperationsRoute.addChildren([
          branchOperationsRedirectRoute,
          branchOperationsResourcesRoute,
          branchOperationsManagementRoute,
        ]),
        branchExplorerLayout.addChildren([
          branchExplorerRoutes.general,
          branchExplorerRoutes.query,
          branchExplorerRoutes.object,
        ]),
      ]),
      securityRoute.addChildren([
        securityRedirectRoute,
        securityVpcRoute,
        securityPrivateEndpointsRoute,
        securityIpAllowListRoute,
        securityAuthenticationRoute,
      ]),
      cliMcpLayout.addChildren([
        cliMcpRedirectRoute,
        cliMcpCLIRoute,
        cliMcpMCPRoute,
      ]),
      integrationsRoute,
      activityRoute,
      membersRoute,
      dataViewRoute,
      playRoute,
      supportRoute.addChildren([
        supportRedirectRoute,
        supportMainRoute,
        supportCasesRoute,
      ]),
      supportEmailRoute,
      errorRoute,
    ]),
    // projectId-less routes (static siblings of projectLayout).
    joinProjectRoute,
    accountSettingsRoute,
  ]),
  registerRedirectRoute,
  signinRedirectRoute,
  idpRoute,
  logoutRoute,
  welcomeRoute,
  legacyDashboardRoute,
  legacyDashboardSplatRoute,
  publicLayout.addChildren([
    indexRedirectRoute,
    loginRoute,
    signupRoute,
    signupSuccessRoute,
    verifyRoute,
    publicErrorRoute,
  ]),
  successLayout.addChildren([
    resetPasswordRoute,
    verifyBillingRoute,
    idpErrorRoute,
    unsupportedBrowserRoute,
  ]),
  successAuthLayout.addChildren([
    oAuthAuthorizationRoute,
    oAuthSuccessRoute,
    oAuthDeviceRoute,
  ]),
  notFoundRoute,
]);

export const router = createRouter({
  routeTree,
  history,
  context: {
    auth: undefined!,
    client: client,
  },
  // Per-route error boundary: contains a crash to that route and reports it once.
  defaultErrorComponent: ({ error, reset, info }) => (
    <ErrorCatch
      error={error}
      resetError={reset}
      componentStack={info?.componentStack}
    />
  ),
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  defaultPendingMs: 200,
  // Nested routes each show their own pendingComponent, one inside the other,
  // and a minimum display time would add up across levels and hold back real
  // content. Skeletons match the content they stand in for, so a briefly
  // shown one reads as the page assembling rather than as a flash.
  defaultPendingMinMs: 0,
  InnerWrap: BootShellUntilRootLoads,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
